import type { DSAttendanceContainerOrderBy } from '@/types/ds-attendance.types'

// ============================================================================
// ENUMS DE DOMÍNIO (Contrato com o Backend - Única Fonte de Verdade)
// ============================================================================

export enum DSAttendanceStatus {
  PENDING_SUBMISSION = 'PENDING_SUBMISSION',
  PENDING_AUDIT = 'PENDING_AUDIT',
  AUDIT_IN_PROGRESS = 'AUDIT_IN_PROGRESS',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  DELETED = 'DELETED'
}

export enum DSAttendanceMonitoringType {
  FOLHA = 'FOLHA',
  EXTERNAL_APP = 'EXTERNAL_APP',
  MINHA_ESCALA_APP = 'MINHA_ESCALA_APP',
  NON_DAILY_SHEET = 'NON_DAILY_SHEET'
}

export enum ExternalApp {
  DOCTOR_ID = 'DOCTOR_ID',
  PEGA_PLANTAO = 'PEGA_PLANTAO',
  OTHER = 'OTHER'
}

export const hasRulesAndTemplates = (type: string): boolean =>
  [
    DSAttendanceMonitoringType.FOLHA,
    DSAttendanceMonitoringType.EXTERNAL_APP,
    DSAttendanceMonitoringType.NON_DAILY_SHEET
  ].includes(type as DSAttendanceMonitoringType)

export enum CheckMethod {
  GEOLOCATION = 'GEOLOCATION',
  MANUAL = 'MANUAL',
  BIOMETRIC = 'BIOMETRIC'
}

export const CHECK_METHOD_LABELS: Record<CheckMethod, string> = {
  [CheckMethod.GEOLOCATION]: 'Geolocalização (App)',
  [CheckMethod.MANUAL]: 'Manual (Painel Web)',
  [CheckMethod.BIOMETRIC]: 'Geolocalização com Biometria'
}

export const CHECK_METHOD_OPTIONS = [
  { title: 'Geolocalização', value: CheckMethod.GEOLOCATION },
  // { title: 'Manual (Painel Web)', value: CheckMethod.MANUAL },
  { title: 'Geolocalização com Biometria', value: CheckMethod.BIOMETRIC }
] as const

export enum DSAttendanceDeadlineCalculationType {
  BUSINESS_HOURS = 'BUSINESS_HOURS',
  CONSECUTIVE_HOURS = 'CONSECUTIVE_HOURS'
}

export enum DSAttendanceRuleRecurrence {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY'
}

export enum DSAttendanceAuditType {
  CONTAINER_CREATE = 'CONTAINER_CREATE',
  CONTAINER_SUBMIT = 'CONTAINER_SUBMIT',
  CONTAINER_APPROVE = 'CONTAINER_APPROVE',
  CONTAINER_REJECT = 'CONTAINER_REJECT',
  CONTAINER_LOCK = 'CONTAINER_LOCK',
  CONTAINER_UNLOCK = 'CONTAINER_UNLOCK',
  CONTAINER_START_AUDIT = 'CONTAINER_START_AUDIT',
  CONTAINER_AUDIT_IN_PROGRESS = 'CONTAINER_AUDIT_IN_PROGRESS',
  CONTAINER_DELETE = 'CONTAINER_DELETE',
  CONTAINER_RESUBMIT = 'CONTAINER_RESUBMIT',
  APPROVAL_REVERTED = 'APPROVAL_REVERTED',
  JUSTIFICATION_SUBMITTED = 'JUSTIFICATION_SUBMITTED',
  JUSTIFICATION_ACCEPTED = 'JUSTIFICATION_ACCEPTED',
  JUSTIFICATION_REJECTED = 'JUSTIFICATION_REJECTED',
  ENTRY_UPDATE = 'ENTRY_UPDATE',
  EVIDENCE_UPLOAD = 'EVIDENCE_UPLOAD',
  EVIDENCE_DELETE = 'EVIDENCE_DELETE',
  POLICY_CHANGE = 'POLICY_CHANGE',
  COMMENT_CREATE = 'COMMENT_CREATE',
  COMMENT_DELETE = 'COMMENT_DELETE',
  /** ESP-01/28-03 — conciliação automática de uma entry SINGLE_MATCH (QW-01). */
  ENTRY_AUTOMATIC_CONCILIATION = 'ENTRY_AUTOMATIC_CONCILIATION',
  /** ESP-01/28-03 — "Desfazer" de uma conciliação automática pelo auditor. */
  AUTOMATIC_CONCILIATION_REVERSED = 'AUTOMATIC_CONCILIATION_REVERSED',
  /**
   * BUG-03 — telemetria interna do shadow mode (fase 29/ESP-03), gravada a cada
   * conciliação mas NUNCA exibida ao operador — ver
   * `HIDDEN_FROM_TIMELINE_AUDIT_TYPES` em `dsAttendanceHistory.ts`.
   */
  ENTRY_SHADOW_SCORE = 'ENTRY_SHADOW_SCORE',
  /**
   * BUG-01 (HML 2026-09-02) — a identidade crua da linha (rawCrm/rawDoctorName)
   * mudou num merge de `conciliate()`. É decisão VISÍVEL ao operador: a linha
   * trocou de médico, então o histórico precisa dizer isso em pt-BR.
   */
  ENTRY_RAW_IDENTITY_CHANGED = 'ENTRY_RAW_IDENTITY_CHANGED',
  /** ESP-02/29-04 — entries pré-geradas a partir das DoctorStation do dia (espelho da escala). */
  CONTAINER_ENTRIES_PREFILLED = 'CONTAINER_ENTRIES_PREFILLED',
  /**
   * ESP-02/29-04 (decisão 5 do grilling) — registro ÚNICO da conferência da folha
   * gravado pelo OK global. O nome EN não usa "attestation" de propósito: em pt-BR
   * o termo é "conferência da folha", NUNCA "atestado" (reservado a atestado médico).
   */
  GLOBAL_PREFILL_SHEET_CONFERENCE = 'GLOBAL_PREFILL_SHEET_CONFERENCE',
  /** ESP-02/29-04 (decisão 2 do grilling) — previsto na escala que não consta na folha. */
  ENTRY_MARKED_ABSENT = 'ENTRY_MARKED_ABSENT',
  /** Desfaz o `ENTRY_MARKED_ABSENT` — a linha volta ao estado de pós-espelho. */
  ENTRY_ABSENCE_REVERTED = 'ENTRY_ABSENCE_REVERTED',
  /**
   * Fase 2 (spec 2026-09-11 §7 item 4) — checklist de envio marcado por quem subiu a folha.
   * `newValues` traz os 4 booleanos do checklist.
   */
  UPLOAD_SELF_CHECK = 'UPLOAD_SELF_CHECK',
  /** Fase 2 (§6.4) — o motor aprovou o registro sem passar pela fila manual. */
  CONTAINER_AUTO_APPROVED = 'CONTAINER_AUTO_APPROVED',
  /**
   * Fase 2 (§6.3) — um critério falhou (ou o registro foi sorteado na amostragem) e o
   * container caiu na fila manual. `newValues.reason` carrega o motivo.
   */
  CONTAINER_MANUAL_REVIEW_REQUIRED = 'CONTAINER_MANUAL_REVIEW_REQUIRED',
  /** F2 (§3.8) — Registro de Atividade enviado pelo emissor. */
  ACTIVITY_SUBMITTED = 'ACTIVITY_SUBMITTED',
  /** F2 (§3.8) — o auditor mudou o horário declarado; o declarado continua guardado. */
  ACTIVITY_OVERRIDDEN = 'ACTIVITY_OVERRIDDEN',
  /** F2 (§3.8) — Registro de Atividade aprovado. */
  ACTIVITY_APPROVED = 'ACTIVITY_APPROVED',
  /** F2 (§3.8) — aprovado depois do fechamento do período. */
  ACTIVITY_APPROVED_AFTER_CLOSURE = 'ACTIVITY_APPROVED_AFTER_CLOSURE',
  /** F2 (§3.8) — reenvio referenciando um registro rejeitado (Q20). */
  ACTIVITY_RESUBMITTED = 'ACTIVITY_RESUBMITTED',
  /** F2 (§3.8) — registro superado por um reenvio posterior. */
  ACTIVITY_SUPERSEDED_BY_RESUBMISSION = 'ACTIVITY_SUPERSEDED_BY_RESUBMISSION',
  /** F2 (§5.3) — linha nascida da migração retroativa dos checks `WEB` com anexo. */
  LEGACY_MIGRATED = 'LEGACY_MIGRATED',
  /**
   * F3 (§3.8) — o horário efetivo de uma perna coberta por este Registro de Atividade
   * trocou de fonte para a folha auditada (Q19). O container continua APROVADO.
   */
  ACTIVITY_SUPERSEDED_BY_SHEET = 'ACTIVITY_SUPERSEDED_BY_SHEET',
  /** F3 (§3.8) — o job do minuto 7 abriu uma Solicitação de Registro de Atividade. */
  REQUEST_CREATED = 'REQUEST_CREATED',
  /** F3 (§3.8) — a solicitação foi preenchida por um envio do médico/emissor. */
  REQUEST_FULFILLED = 'REQUEST_FULFILLED',
  /** F3 (§3.8) — a perna cobrada foi coberta por outro canal; `justification` diz qual. */
  REQUEST_CANCELLED = 'REQUEST_CANCELLED',
  /** F3 (§3.8) — a rejeição do registro que preencheu a solicitação a reabriu (Q20). */
  REQUEST_REOPENED = 'REQUEST_REOPENED',
  /** Spec 2026-09-25 §4.1.7 — o recálculo das abertas reagendou o prazo. */
  REQUEST_RESCHEDULED = 'REQUEST_RESCHEDULED',
  /**
   * Spec 2026-09-24 §7 — sobrescrita de perna entre registros do mesmo plantão.
   * `LEGS_OVERWRITTEN`: no registro sendo aprovado, 1 por plantão em conflito.
   */
  LEGS_OVERWRITTEN = 'LEGS_OVERWRITTEN',
  /** No registro que PERDEU alguma perna. `newValues.byContainerId` = quem venceu. */
  LEGS_OVERWRITTEN_BY = 'LEGS_OVERWRITTEN_BY',
  /** No registro que MANTEVE a perna por decisão `KEEP`. */
  LEGS_OVERWRITE_KEPT = 'LEGS_OVERWRITE_KEPT',
  /** Nas contrapartes, quando a reversão de um registro desfaz as fixações dele. */
  LEGS_OVERWRITE_UNDONE = 'LEGS_OVERWRITE_UNDONE',
  /** rejected-sheet-resubmission: a Auditoria anexou nova folha ao registro reprovado. */
  NEW_SHEET_SUBMITTED = 'NEW_SHEET_SUBMITTED'
}

export const DS_ATTENDANCE_AUDIT_TYPE_LABEL: Record<DSAttendanceAuditType, string> = {
  [DSAttendanceAuditType.CONTAINER_CREATE]: 'Registro criado',
  [DSAttendanceAuditType.CONTAINER_SUBMIT]: 'Registro enviado',
  [DSAttendanceAuditType.CONTAINER_APPROVE]: 'Registro aprovado',
  [DSAttendanceAuditType.CONTAINER_REJECT]: 'Registro reprovado',
  [DSAttendanceAuditType.CONTAINER_LOCK]: 'Registro bloqueado',
  [DSAttendanceAuditType.CONTAINER_UNLOCK]: 'Registro desbloqueado',
  [DSAttendanceAuditType.CONTAINER_START_AUDIT]: 'Auditoria iniciada',
  [DSAttendanceAuditType.CONTAINER_AUDIT_IN_PROGRESS]: 'Auditoria em progresso',
  [DSAttendanceAuditType.CONTAINER_DELETE]: 'Registro excluído',
  [DSAttendanceAuditType.CONTAINER_RESUBMIT]: 'Registro reenviado',
  [DSAttendanceAuditType.APPROVAL_REVERTED]: 'Aprovação revertida',
  [DSAttendanceAuditType.JUSTIFICATION_SUBMITTED]: 'Justificativa enviada',
  [DSAttendanceAuditType.JUSTIFICATION_ACCEPTED]: 'Justificativa aceita',
  [DSAttendanceAuditType.JUSTIFICATION_REJECTED]: 'Justificativa rejeitada',
  [DSAttendanceAuditType.ENTRY_UPDATE]: 'Lançamento atualizado',
  [DSAttendanceAuditType.EVIDENCE_UPLOAD]: 'Evidência enviada',
  [DSAttendanceAuditType.EVIDENCE_DELETE]: 'Evidência excluída',
  [DSAttendanceAuditType.POLICY_CHANGE]: 'Política alterada',
  [DSAttendanceAuditType.COMMENT_CREATE]: 'Comentário criado',
  [DSAttendanceAuditType.COMMENT_DELETE]: 'Comentário excluído',
  [DSAttendanceAuditType.ENTRY_AUTOMATIC_CONCILIATION]: 'Conciliação automática',
  [DSAttendanceAuditType.AUTOMATIC_CONCILIATION_REVERSED]: 'Conciliação automática desfeita',
  // BUG-03 — label só por completude do Record; este tipo nunca chega a ser exibido
  // (filtrado em `mapAuditTrailToHistoryEvents`, `dsAttendanceHistory.ts`).
  [DSAttendanceAuditType.ENTRY_SHADOW_SCORE]: 'Score sombra (interno)',
  [DSAttendanceAuditType.ENTRY_RAW_IDENTITY_CHANGED]: 'Identificação da linha corrigida',
  [DSAttendanceAuditType.CONTAINER_ENTRIES_PREFILLED]: 'Linhas pré-preenchidas com a escala',
  [DSAttendanceAuditType.GLOBAL_PREFILL_SHEET_CONFERENCE]: 'Conferência da folha confirmada',
  [DSAttendanceAuditType.ENTRY_MARKED_ABSENT]: 'Linha marcada como não consta na folha',
  [DSAttendanceAuditType.ENTRY_ABSENCE_REVERTED]: 'Linha restaurada para a folha',
  [DSAttendanceAuditType.UPLOAD_SELF_CHECK]: 'Checklist de envio preenchido pelo emissor',
  [DSAttendanceAuditType.CONTAINER_AUTO_APPROVED]: 'Aprovado automaticamente',
  [DSAttendanceAuditType.CONTAINER_MANUAL_REVIEW_REQUIRED]: 'Enviado para revisão manual',
  [DSAttendanceAuditType.ACTIVITY_SUBMITTED]: 'Registro de Atividade enviado',
  // §3.8 é o texto canônico do histórico; "Horário declarado alterado pelo
  // auditor" (o rótulo original da W1) divergia da spec e da tela de auditoria.
  [DSAttendanceAuditType.ACTIVITY_OVERRIDDEN]: 'Valor sobrescrito pelo auditor',
  [DSAttendanceAuditType.ACTIVITY_APPROVED]: 'Registro de Atividade aprovado',
  [DSAttendanceAuditType.ACTIVITY_APPROVED_AFTER_CLOSURE]:
    'Registro de Atividade aprovado após o fechamento',
  [DSAttendanceAuditType.ACTIVITY_RESUBMITTED]: 'Registro de Atividade reenviado',
  [DSAttendanceAuditType.ACTIVITY_SUPERSEDED_BY_RESUBMISSION]:
    'Registro de Atividade substituído por um reenvio',
  [DSAttendanceAuditType.LEGACY_MIGRATED]: 'Registro migrado do fluxo antigo',
  // F3 (§3.8). O motivo do cancelamento ("perna coberta por folha aprovada") chega no
  // `justification` do audit e o `mapAuditToHistoryEvent` já o anexa ao rótulo — por isso
  // o texto abaixo termina em "cancelada", sem o complemento da fonte.
  [DSAttendanceAuditType.ACTIVITY_SUPERSEDED_BY_SHEET]: 'Superado pela folha auditada',
  [DSAttendanceAuditType.REQUEST_CREATED]: 'Solicitação automática aberta',
  [DSAttendanceAuditType.REQUEST_FULFILLED]: 'Solicitação automática encerrada pelo envio',
  [DSAttendanceAuditType.REQUEST_CANCELLED]: 'Solicitação automática cancelada',
  [DSAttendanceAuditType.REQUEST_REOPENED]: 'Solicitação automática reaberta após a rejeição',
  [DSAttendanceAuditType.REQUEST_RESCHEDULED]: 'Prazo da solicitação recalculado',
  [DSAttendanceAuditType.LEGS_OVERWRITTEN]: 'Sobrescrita de perna confirmada',
  [DSAttendanceAuditType.LEGS_OVERWRITTEN_BY]: 'Perna sobrescrita por outro registro',
  [DSAttendanceAuditType.LEGS_OVERWRITE_KEPT]: 'Perna mantida na sobrescrita',
  [DSAttendanceAuditType.LEGS_OVERWRITE_UNDONE]: 'Sobrescrita de perna desfeita',
  [DSAttendanceAuditType.NEW_SHEET_SUBMITTED]: 'Nova folha enviada'
}

// ============================================================================
// MAPEAMENTO DE TRADUÇÃO (Frontend - Labels para o Utilizador)
// ============================================================================

export const DS_ATTENDANCE_STATUS_LABELS: Record<DSAttendanceStatus, string> = {
  [DSAttendanceStatus.PENDING_SUBMISSION]: 'Aguardando Envio',
  [DSAttendanceStatus.PENDING_AUDIT]: 'Aguardando Auditoria',
  [DSAttendanceStatus.AUDIT_IN_PROGRESS]: 'Auditoria em Progresso',
  [DSAttendanceStatus.APPROVED]: 'Aprovado',
  [DSAttendanceStatus.REJECTED]: 'Reprovado',
  [DSAttendanceStatus.DELETED]: 'Excluído'
}

export enum ImageState {
  COMPLETE = 'COMPLETE',
  ILLEGIBLE = 'ILLEGIBLE',
  PENDING = 'PENDING'
}

export const IMAGE_STATE_LABEL: Record<ImageState, string> = {
  [ImageState.COMPLETE]: 'Completo',
  [ImageState.ILLEGIBLE]: 'Ilegível',
  [ImageState.PENDING]: 'Pendente'
}

// ============================================================================
// OPÇÕES DE INTERFACE (Para Selects e Filtros)
// ============================================================================

export const DS_ATTENDANCE_STATUS_OPTIONS = Object.values(DSAttendanceStatus).map((status) => ({
  label: DS_ATTENDANCE_STATUS_LABELS[status],
  value: status
}))

export const MONITORING_TYPE_LABELS: Record<DSAttendanceMonitoringType, string> = {
  [DSAttendanceMonitoringType.FOLHA]: 'Folha de monitoramento',
  [DSAttendanceMonitoringType.EXTERNAL_APP]: 'Aplicativo externo',
  [DSAttendanceMonitoringType.MINHA_ESCALA_APP]: 'Minha Escala App',
  [DSAttendanceMonitoringType.NON_DAILY_SHEET]: 'Folha não diária'
}

export const DSATTENDANCE_MONITORING_TYPE_OPTIONS = Object.values(DSAttendanceMonitoringType).map(
  (type) => ({
    title: MONITORING_TYPE_LABELS[type],
    value: type
  })
)

export const EXTERNAL_APP_OPTIONS = [
  { title: 'Doctor ID', value: ExternalApp.DOCTOR_ID },
  { title: 'Pega Plantão', value: ExternalApp.PEGA_PLANTAO },
  { title: 'Outro', value: ExternalApp.OTHER }
] as const

export const EXTERNAL_APP_LABELS: Record<ExternalApp, string> = {
  [ExternalApp.DOCTOR_ID]: 'Doctor ID',
  [ExternalApp.PEGA_PLANTAO]: 'Pega Plantão',
  [ExternalApp.OTHER]: 'Outro'
}

export function formatMonitoringTypeLabel(
  monitoringType: DSAttendanceMonitoringType | string | null,
  sourceApp?: string | null
): string {
  if (!monitoringType) return ''
  const baseLabel =
    MONITORING_TYPE_LABELS[monitoringType as DSAttendanceMonitoringType] ?? monitoringType
  if (monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP && sourceApp) {
    const appLabel = EXTERNAL_APP_LABELS[sourceApp as ExternalApp] ?? sourceApp
    return `${baseLabel} - ${appLabel}`
  }
  return baseLabel
}

export const DSATTENDANCE_DEADLINE_CALCULATION_OPTIONS = [
  { title: 'Horas Úteis', value: DSAttendanceDeadlineCalculationType.BUSINESS_HOURS },
  { title: 'Horas Corridas', value: DSAttendanceDeadlineCalculationType.CONSECUTIVE_HOURS }
] as const

export const DSATTENDANCE_RECURRENCE_OPTIONS = [
  { title: 'Semanal', value: DSAttendanceRuleRecurrence.WEEKLY },
  { title: 'Quinzenal', value: DSAttendanceRuleRecurrence.BIWEEKLY },
  { title: 'Mensal', value: DSAttendanceRuleRecurrence.MONTHLY }
] as const

// ============================================================================
// REJEIÇÃO / VALIDAÇÃO / CONCILIAÇÃO
// ============================================================================

/**
 * Frente E/Q20 — rótulo único do motivo "falta de carimbo e assinatura", compartilhado
 * pela rejeição da folha, pela do Registro de Atividade e pelo preset de divergência da
 * conciliação.
 */
export const MISSING_STAMP_AND_SIGNATURE_LABEL = 'Falta de carimbo e assinatura'

export enum RejectionReason {
  INCOMPLETE_SHEET = 'INCOMPLETE_SHEET',
  REDIGITIZE = 'REDIGITIZE',
  DIVERGENCE_SHEET_APP = 'DIVERGENCE_SHEET_APP',
  OPEN_VACANCY = 'OPEN_VACANCY',
  NOT_IDENTIFIED = 'NOT_IDENTIFIED',
  SPECIALTY_FINISHED = 'SPECIALTY_FINISHED',
  INCOMPLETE_CONCILIATION = 'INCOMPLETE_CONCILIATION',
  INCOMPLETE_PRINT = 'INCOMPLETE_PRINT',
  INCORRECT_REPORT_PERIOD = 'INCORRECT_REPORT_PERIOD',
  DIVERGENCE_APP_PLANNING = 'DIVERGENCE_APP_PLANNING',
  REDO_SHEET = 'REDO_SHEET',
  MISSING_STAMP_AND_SIGNATURE = 'MISSING_STAMP_AND_SIGNATURE'
}

export const REJECTION_REASON_LABELS: Record<RejectionReason, string> = {
  [RejectionReason.INCOMPLETE_SHEET]: 'Folha incompleta',
  [RejectionReason.REDIGITIZE]: 'Redigitalizar',
  [RejectionReason.DIVERGENCE_SHEET_APP]: 'Divergência entre folha e app',
  [RejectionReason.OPEN_VACANCY]: 'Vaga aberta',
  [RejectionReason.NOT_IDENTIFIED]: 'Não identificado',
  [RejectionReason.SPECIALTY_FINISHED]: 'Especialidade finalizada',
  [RejectionReason.INCOMPLETE_CONCILIATION]: 'Conciliação incompleta',
  [RejectionReason.INCOMPLETE_PRINT]: 'Print incompleto',
  [RejectionReason.INCORRECT_REPORT_PERIOD]: 'Período do relatório incorreto',
  [RejectionReason.DIVERGENCE_APP_PLANNING]: 'Divergência entre app e planejamento',
  [RejectionReason.REDO_SHEET]: 'Refazer folha',
  [RejectionReason.MISSING_STAMP_AND_SIGNATURE]: MISSING_STAMP_AND_SIGNATURE_LABEL
}

export const REJECTION_REASON_OPTIONS = Object.values(RejectionReason).map((r) => ({
  title: REJECTION_REASON_LABELS[r],
  value: r
}))

export const REJECTION_REASONS_BY_MODALITY: Record<DSAttendanceMonitoringType, RejectionReason[]> =
  {
    [DSAttendanceMonitoringType.FOLHA]: [
      RejectionReason.DIVERGENCE_SHEET_APP,
      RejectionReason.INCOMPLETE_SHEET,
      RejectionReason.REDIGITIZE,
      RejectionReason.REDO_SHEET,
      RejectionReason.MISSING_STAMP_AND_SIGNATURE
    ],
    [DSAttendanceMonitoringType.EXTERNAL_APP]: [
      RejectionReason.INCOMPLETE_PRINT,
      RejectionReason.INCORRECT_REPORT_PERIOD,
      RejectionReason.NOT_IDENTIFIED,
      RejectionReason.DIVERGENCE_APP_PLANNING,
      RejectionReason.INCOMPLETE_CONCILIATION,
      RejectionReason.SPECIALTY_FINISHED,
      RejectionReason.OPEN_VACANCY
    ],
    [DSAttendanceMonitoringType.MINHA_ESCALA_APP]: [
      RejectionReason.OPEN_VACANCY,
      RejectionReason.NOT_IDENTIFIED,
      RejectionReason.SPECIALTY_FINISHED,
      RejectionReason.INCOMPLETE_CONCILIATION,
      RejectionReason.DIVERGENCE_SHEET_APP,
      RejectionReason.MISSING_STAMP_AND_SIGNATURE
    ],
    [DSAttendanceMonitoringType.NON_DAILY_SHEET]: [
      RejectionReason.DIVERGENCE_SHEET_APP,
      RejectionReason.INCOMPLETE_SHEET,
      RejectionReason.REDIGITIZE,
      RejectionReason.REDO_SHEET,
      RejectionReason.MISSING_STAMP_AND_SIGNATURE
    ]
  }

export function getRejectionReasonOptionsByModality(
  monitoringType?: string | null
): Array<{ title: string; value: string }> {
  const key = monitoringType as DSAttendanceMonitoringType
  const reasons =
    key && key in REJECTION_REASONS_BY_MODALITY
      ? REJECTION_REASONS_BY_MODALITY[key]
      : REJECTION_REASONS_BY_MODALITY[DSAttendanceMonitoringType.FOLHA]
  return reasons.map((r) => ({ title: REJECTION_REASON_LABELS[r], value: r }))
}

export enum AuditInProgressReason {
  DOCTOR_SWAP = 'DOCTOR_SWAP',
  SCHEDULE_DIVERGENCE = 'SCHEDULE_DIVERGENCE'
}

export const AUDIT_IN_PROGRESS_REASON_LABELS: Record<AuditInProgressReason, string> = {
  [AuditInProgressReason.DOCTOR_SWAP]: 'Troca médica',
  [AuditInProgressReason.SCHEDULE_DIVERGENCE]: 'Divergência de escalas'
}

export const AUDIT_IN_PROGRESS_REASON_OPTIONS = Object.values(AuditInProgressReason).map((r) => ({
  title: AUDIT_IN_PROGRESS_REASON_LABELS[r],
  value: r
}))

export enum ValidationStatus {
  PENDING = 'PENDING',
  CONCILIATED = 'CONCILIATED',
  DIVERGENT = 'DIVERGENT'
}

/**
 * Frente E/Q20 — motivo gravado quando a linha vai para DIVERGENT sem o auditor escolher
 * outro. É o texto que a tela já gravava antes do preset: quem não mexe, grava o mesmo.
 */
export const DEFAULT_DIVERGENCE_REASON = 'Sem correspondência encontrada'

/** Frente E/Q20 — motivos prontos de divergência de linha na conciliação (texto livre no backend). */
export enum DivergenceReasonPreset {
  NO_MATCH = 'NO_MATCH',
  MISSING_STAMP_AND_SIGNATURE = 'MISSING_STAMP_AND_SIGNATURE'
}

export const DIVERGENCE_REASON_PRESET_LABELS: Record<DivergenceReasonPreset, string> = {
  [DivergenceReasonPreset.NO_MATCH]: DEFAULT_DIVERGENCE_REASON,
  [DivergenceReasonPreset.MISSING_STAMP_AND_SIGNATURE]: MISSING_STAMP_AND_SIGNATURE_LABEL
}

export const DIVERGENCE_REASON_PRESETS: ReadonlyArray<{
  key: DivergenceReasonPreset
  label: string
}> = Object.values(DivergenceReasonPreset).map((key) => ({
  key,
  label: DIVERGENCE_REASON_PRESET_LABELS[key]
}))

export enum DSAttendanceVigencyStatus {
  IN_EFFECT = 'IN_EFFECT',
  EXPIRED = 'EXPIRED',
  FUTURE = 'FUTURE',
  NO_LINK = 'NO_LINK'
}

export enum ConciliationType {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL'
}

// ============================================================================
// ESPELHO DA ESCALA + CONFERÊNCIA DA FOLHA (ESP-02/29-04)
// ============================================================================

/**
 * Origem da linha da folha. `PREFILLED` nasceu do espelho da escala (prefill das
 * `DoctorStation` do dia); `MANUAL` passou pela digitação de um humano. Espelha
 * `SourceType` do backend — não inventar valor novo aqui.
 */
export enum SourceType {
  PREFILLED = 'PREFILLED',
  MANUAL = 'MANUAL'
}

/**
 * Veredito de assertividade da linha nascida do prefill (decisão 6 do grilling):
 * `CONFIRMED` (espelho acertou), `EDITED` (algum valor mudou), `ABSENT` (previsto
 * que não consta na folha) e `EXTRA` (linha na folha sem previsão na escala).
 */
export enum PrefillOutcome {
  CONFIRMED = 'CONFIRMED',
  EDITED = 'EDITED',
  ABSENT = 'ABSENT',
  EXTRA = 'EXTRA'
}

/** Rótulo curto do chip de estado de cada linha da grade (mockup 05, coluna "Estado da linha"). */
export const PREFILL_OUTCOME_CHIP_LABELS: Record<PrefillOutcome, string> = {
  [PrefillOutcome.CONFIRMED]: 'Confere',
  [PrefillOutcome.EDITED]: 'Editado',
  [PrefillOutcome.ABSENT]: 'Não consta na folha',
  [PrefillOutcome.EXTRA]: 'Extra'
}

/** Rótulo da contagem por desfecho no modal de conferência (mockup 05, estado 2). */
export const PREFILL_OUTCOME_TALLY_LABELS: Record<PrefillOutcome, string> = {
  [PrefillOutcome.CONFIRMED]: 'Conferem',
  [PrefillOutcome.EDITED]: 'Editadas',
  [PrefillOutcome.ABSENT]: 'Não constam',
  [PrefillOutcome.EXTRA]: 'Extras'
}

/** Chip da linha ainda intocada — não é um `PrefillOutcome`: é a ausência de veredito. */
export const PREFILLED_ROW_CHIP_LABEL = 'Pré-preenchido'

/**
 * Texto LITERAL da conferência da folha exibido no modal do OK global. Igual ao
 * `GLOBAL_PREFILL_SHEET_CONFERENCE_TEXT` do backend, que é o que vai para a trilha —
 * o operador tem de ler na tela exatamente o que fica gravado.
 *
 * NUNCA chamar isto de "atestado" em texto de UI: o termo aprovado (usuário, 03/09) é
 * "conferência da folha" / "Confirmar conferência" — "atestado" colide com atestado médico.
 */
export const SHEET_CONFERENCE_TEXT =
  'Conferi as assinaturas e os carimbos na folha física e confirmo que as linhas ' +
  'pré-preenchidas conferem com a escala.'

/** Teto de containers por requisição de aprovação em lote (backend: `@ArrayMaxSize(50)`). */
export const BULK_APPROVE_MAX_CONTAINERS = 50

/**
 * Ordenação padrão da fila (pacote 2026-09-10): Antecipe > À vista > plantão mais antigo.
 *
 * Constante compartilhada de propósito: a lista só escreve `sort` na URL quando ele DIFERE
 * deste padrão, então o "próximo da fila" (`useNextContainer`) precisa do mesmo valor para
 * não cair no `referenceDate ASC` do backend e devolver um registro de outra ordem.
 */
export const DEFAULT_CONTAINER_ORDER_BY: DSAttendanceContainerOrderBy = 'PAYMENT_PRIORITY'

/**
 * Ordenação padrão quando a URL não traz `sort`: `SLA` nas abas Todos/Folha/Atividade
 * (spec 2026-10-01, F17) e `DEFAULT_CONTAINER_ORDER_BY` na aba Solicitações (CA9). A lista
 * (`Index.vue`) e o "próximo" (`useNextContainer`) usam este mesmo helper.
 */
export const defaultContainerOrderBy = (onlyRequests: boolean): DSAttendanceContainerOrderBy =>
  onlyRequests ? DEFAULT_CONTAINER_ORDER_BY : 'SLA'

// ============================================================================
// MENSAGENS / TOOLTIPS
// ============================================================================

export const STATION_TOOLTIP =
  'Esse campo não é equivalente às<br/>posições do Minha Escala e não será<br/>considerado para a conciliação'

/* ------------------------------------------------------------------------- *
 * F2 — Registro de Atividade. Espelho EXATO de
 * `src/modules/ds-attendance/domain/ds-attendance.constants.ts` no backend.
 * O repo já duplica enums assim (`DSAttendanceStatus`, `CheckMethod`); a regra
 * é: mesmo VALOR, rótulo pt-BR só aqui.
 *
 * Vocabulário (Q1/Q13): nenhum rótulo novo usa a palavra "evidência" — o que o
 * médico envia é um ANEXO de um Registro de Atividade. As telas antigas (folha,
 * `EvidenceModal.vue`, fila aposentada) mantêm os textos atuais de propósito.
 * ------------------------------------------------------------------------- */

/**
 * Espécie do container. ORTOGONAL a `DSAttendanceMonitoringType`: o
 * `monitoringType` diz por qual canal o Registro de Plantão chegou; o `kind` diz
 * que espécie de registro é. Em `ATIVIDADE`, `monitoringType` vem nulo.
 */
export enum ContainerKind {
  FOLHA = 'FOLHA',
  ATIVIDADE = 'ATIVIDADE'
}

/** Rótulo pt-BR da espécie — o nome das abas da fila e do chip "Aba" do modal de exportação. */
export const CONTAINER_KIND_LABELS: Record<ContainerKind, string> = {
  [ContainerKind.FOLHA]: 'Folha',
  [ContainerKind.ATIVIDADE]: 'Atividade'
}

/** Quais pernas do plantão um Registro de Atividade pretende cobrir (Q15). */
export enum CoveredLegs {
  CHECKIN = 'CHECKIN',
  CHECKOUT = 'CHECKOUT',
  BOTH = 'BOTH'
}

export const COVERED_LEGS_LABELS: Record<CoveredLegs, string> = {
  [CoveredLegs.CHECKIN]: 'Check-in',
  [CoveredLegs.CHECKOUT]: 'Check-out',
  [CoveredLegs.BOTH]: 'Ambos'
}

/** Catálogo GLOBAL de tipos (Q10). Os aceitos são configurados por escala. */
export enum ActivityType {
  PATIENT_CARE_REPORT = 'PATIENT_CARE_REPORT',
  CHECK_APP_SCREENSHOT = 'CHECK_APP_SCREENSHOT',
  UNIT_ATTENDANCE_CERTIFICATE = 'UNIT_ATTENDANCE_CERTIFICATE',
  APP_FAILURE_REPORT = 'APP_FAILURE_REPORT',
  PARKING_TICKET = 'PARKING_TICKET',
  ADMIN_ADJUSTMENT = 'ADMIN_ADJUSTMENT',
  OTHER = 'OTHER'
}

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  [ActivityType.PATIENT_CARE_REPORT]: 'Relatório de atendimento de paciente',
  [ActivityType.CHECK_APP_SCREENSHOT]: 'Print do aplicativo de check',
  [ActivityType.UNIT_ATTENDANCE_CERTIFICATE]: 'Atestado de comparecimento assinado pela unidade',
  [ActivityType.APP_FAILURE_REPORT]: 'Relatório de falha no aplicativo',
  [ActivityType.PARKING_TICKET]: 'Ticket de estacionamento',
  [ActivityType.ADMIN_ADJUSTMENT]: 'Ajuste administrativo (backoffice)',
  [ActivityType.OTHER]: 'Outros'
}

/**
 * Motivos de rejeição do Registro de Atividade (§3.6). É o `CheckRejectionReason`
 * da fila que vai ser aposentada com "evidência" → "anexo" e `REDIGITIZE` →
 * `TYPE_NOT_ACCEPTED`: quem audita hoje reencontra os mesmos motivos na fila nova.
 */
export enum ActivityRejectionReason {
  ILLEGIBLE_ATTACHMENT = 'ILLEGIBLE_ATTACHMENT',
  WRONG_STATION = 'WRONG_STATION',
  DIVERGENT_HOURS = 'DIVERGENT_HOURS',
  INCOMPLETE_ATTACHMENT = 'INCOMPLETE_ATTACHMENT',
  TYPE_NOT_ACCEPTED = 'TYPE_NOT_ACCEPTED',
  FRAUD_SUSPICION = 'FRAUD_SUSPICION',
  ATTACHMENT_MISSING_STAMP_AND_SIGNATURE = 'ATTACHMENT_MISSING_STAMP_AND_SIGNATURE',
  OTHER = 'OTHER'
}

export const ACTIVITY_REJECTION_REASON_LABELS: Record<ActivityRejectionReason, string> = {
  [ActivityRejectionReason.ILLEGIBLE_ATTACHMENT]: 'Anexo ilegível',
  [ActivityRejectionReason.WRONG_STATION]: 'Plantão incorreto',
  [ActivityRejectionReason.DIVERGENT_HOURS]: 'Horários divergentes',
  [ActivityRejectionReason.INCOMPLETE_ATTACHMENT]: 'Anexo incompleto',
  [ActivityRejectionReason.TYPE_NOT_ACCEPTED]: 'Tipo não aceito nesta escala',
  [ActivityRejectionReason.FRAUD_SUSPICION]: 'Suspeita de fraude',
  [ActivityRejectionReason.ATTACHMENT_MISSING_STAMP_AND_SIGNATURE]:
    MISSING_STAMP_AND_SIGNATURE_LABEL,
  [ActivityRejectionReason.OTHER]: 'Outro'
}

/**
 * Fonte que venceu o ranking de precedência da F1 para UMA perna do plantão.
 * A ordem de declaração é a de M1-1 (a mesma do `DEFAULT_SOURCE_PRECEDENCE` do
 * backend) — a tela lê daqui para mostrar "posição no ranking" sem recalcular.
 */
export enum EffectiveSourceKind {
  SHEET = 'SHEET',
  APP = 'APP',
  EXTERNAL_APP = 'EXTERNAL_APP',
  ACTIVITY_RECORD = 'ACTIVITY_RECORD',
  AUTO_PERMANENCE = 'AUTO_PERMANENCE',
  MANUAL_LEGACY = 'MANUAL_LEGACY',
  LEGACY = 'LEGACY'
}

/**
 * Divergência consciente de rótulo: `ACTIVITY_RECORD` é "Registro de Atividade"
 * aqui e "Registro de Atividade aprovado" no backend. No web o estado já aparece
 * ao lado ("aprovado 12/09") e repeti-lo no nome da fonte polui o card de M1.
 * Registrado para que ninguém "corrija" depois.
 */
export const EFFECTIVE_SOURCE_KIND_LABELS: Record<EffectiveSourceKind, string> = {
  [EffectiveSourceKind.SHEET]: 'Folha auditada',
  [EffectiveSourceKind.APP]: 'App Minha Escala',
  [EffectiveSourceKind.EXTERNAL_APP]: 'App Externo',
  [EffectiveSourceKind.ACTIVITY_RECORD]: 'Registro de Atividade',
  [EffectiveSourceKind.AUTO_PERMANENCE]: 'Permanência automática',
  [EffectiveSourceKind.MANUAL_LEGACY]: 'Manual (legado)',
  [EffectiveSourceKind.LEGACY]: 'Legado'
}

/**
 * Sobrescrita de perna (spec 2026-09-24; BE plan Risco 3) — `source_method` da perna FIXADA
 * pela sobrescrita. Não é método de check (não entra em `CheckMethod`, que é o que a política
 * exige); só aparece na fonte vencedora de uma perna.
 */
export const EFFECTIVE_SOURCE_METHOD_PINNED = 'PINNED'

/**
 * Emenda de plantões sequenciais (spec antecipe-registration 2026-09-30, §3): a entrada de B
 * perdoada ou a saída de A limitada pela emenda. `kind` continua o da fonte vencedora original.
 */
export const EFFECTIVE_SOURCE_METHOD_SEQUENTIAL = 'SEQUENTIAL'

/** Rótulo do `method` da fonte vencedora: fixação da sobrescrita, emenda, método de check ou cru. */
export function effectiveSourceMethodLabel(method: string | null | undefined): string | null {
  if (!method) return null
  if (method === EFFECTIVE_SOURCE_METHOD_PINNED) return 'Definida na sobrescrita'
  if (method === EFFECTIVE_SOURCE_METHOD_SEQUENTIAL) return 'Plantão sequencial'
  return CHECK_METHOD_LABELS[method as CheckMethod] ?? method
}

/** Estado do registro de um plantão, somando as duas pernas (M1, balão ①). */
export enum EffectiveRegistrationStatus {
  OK = 'OK',
  PARTIAL = 'PARTIAL',
  PENDING = 'PENDING',
  NONE = 'NONE',
  /**
   * Q1 (spec 2026-09-25 §2.2) — plantão `our_team = false`: o registro não é exigido.
   * Só o grid do pós-plantão devolve este valor; o horário efetivo da vaga não muda.
   */
  NOT_REQUIRED = 'NOT_REQUIRED'
}

export const EFFECTIVE_REGISTRATION_STATUS_LABELS: Record<EffectiveRegistrationStatus, string> = {
  [EffectiveRegistrationStatus.OK]: 'Registro OK',
  [EffectiveRegistrationStatus.PARTIAL]: 'Parcial',
  [EffectiveRegistrationStatus.PENDING]: 'Pendente',
  [EffectiveRegistrationStatus.NONE]: 'Sem registro',
  [EffectiveRegistrationStatus.NOT_REQUIRED]: 'Não exigido'
}

/**
 * Critérios do `activityRiskScore` (M3-1). São as chaves do `jsonb` que o backend
 * manda em `activityRiskFlags` — critério novo entra como chave nova, sem DDL.
 */
export type ActivityRiskFlag = 'outOfTolerance' | 'otherType' | 'submitterRecentlyRejected'

export const ACTIVITY_RISK_FLAG_LABELS: Record<ActivityRiskFlag, string> = {
  outOfTolerance: 'Horário declarado fora da tolerância',
  otherType: 'Tipo "Outros"',
  submitterRecentlyRejected: 'Emissor com registro recusado recentemente'
}

/* ------------------------------------------------------------------------- *
 * Códigos estáveis da recusa de uma decisão sobre o container. Espelho de
 * `DS_ATTENDANCE_ERROR_CODES` no backend (PR bng-health/minhaescala-backend#2820):
 * o valor é contrato, o texto mostrado ao auditor continua vindo do backend.
 * ------------------------------------------------------------------------- */

/**
 * HTTP 409 — o container JÁ está no status pedido. Não é falha da ação do auditor:
 * é a prova de que ela surtiu efeito (clique duplo, outra aba, outro auditor). Por
 * isso a tela informa em vez de pintar de vermelho — um erro aqui ensinaria a
 * desconfiar de um sucesso. A mensagem do backend traz quando e por quem.
 */
export const DS_ATTENDANCE_CONTAINER_ALREADY_IN_STATUS = 'DS_ATTENDANCE_CONTAINER_ALREADY_IN_STATUS'

/**
 * HTTP 400 — a transição pedida não cabe no status atual (ex.: aprovar o que já foi
 * reprovado). Continua sendo erro de verdade: o toast vermelho carrega a mensagem do
 * backend, e a tela recarrega o detalhe para mostrar a situação que recusou a ação.
 */
export const DS_ATTENDANCE_CONTAINER_INVALID_TRANSITION =
  'DS_ATTENDANCE_CONTAINER_INVALID_TRANSITION'

/**
 * 20 caracteres: a mesma régua já em produção na rejeição da fila antiga, na
 * reversão do pós-plantão e no "Outro" de M3. Dois limites para a mesma ideia é
 * armadilha de suporte.
 */
export const ACTIVITY_DESCRIPTION_MIN_LENGTH = 20

/* ------------------------------------------------------------------------- *
 * F3 — Solicitação automática de Registro de Atividade. Espelho de
 * `src/modules/ds-attendance/domain/activity-request.constants.ts` no backend:
 * mesmo VALOR de enum, rótulo pt-BR só aqui.
 * ------------------------------------------------------------------------- */

/**
 * Canal que a política cobra (M4-2). É DERIVADO de `monitoringType` no backend,
 * nunca digitado: `FOLHA` e `NON_DAILY_SHEET` colapsam em `FOLHA`, porque o prazo
 * das duas conta em dias a partir do dia do plantão.
 */
export enum ActivityRequestChannel {
  MINHA_ESCALA_APP = 'MINHA_ESCALA_APP',
  FOLHA = 'FOLHA',
  EXTERNAL_APP = 'EXTERNAL_APP'
}

export const ACTIVITY_REQUEST_CHANNEL_LABELS: Record<ActivityRequestChannel, string> = {
  [ActivityRequestChannel.MINHA_ESCALA_APP]: 'App Minha Escala',
  [ActivityRequestChannel.FOLHA]: 'Folha (diária / não diária)',
  [ActivityRequestChannel.EXTERNAL_APP]: 'App Externo'
}

/**
 * Perna cobrada pela solicitação. As chaves são as de `CoveredLegs` (o backend manda o
 * mesmo vocabulário em `requestedLeg`), mas o mapa é `Record<string, string>` de
 * propósito: o valor chega como string crua do item da listagem e um canal/perna que o
 * web ainda não conheça tem de cair no `?? valor`, nunca quebrar o card.
 *
 * Não reaproveita `COVERED_LEGS_LABELS` porque o texto de `BOTH` é outro: no envio o
 * médico lê "Ambos" ao lado das duas caixas; na cobrança o card precisa dizer, sozinho,
 * o que está sendo pedido.
 */
export const REQUESTED_LEG_LABELS: Record<string, string> = {
  [CoveredLegs.CHECKIN]: 'Check-in',
  [CoveredLegs.CHECKOUT]: 'Check-out',
  [CoveredLegs.BOTH]: 'Check-in e check-out'
}

/**
 * Defaults da Q9. Só para PREENCHER o placeholder do campo vazio da política — o web
 * NUNCA os materializa no payload: `null` na coluna significa "usar o default do
 * produto" e mudar o default precisa alcançar quem nunca customizou (§6.2).
 */
export const ACTIVITY_REQUEST_DEFAULTS = {
  afterCheckoutHoursApp: 2,
  afterDaysSheet: 3,
  afterHoursExternal: 2
} as const

/** Teto da validação da política (§1.5): 30 dias, em dias e em horas. */
export const ACTIVITY_REQUEST_MAX_DAYS = 30
export const ACTIVITY_REQUEST_MAX_HOURS = 720

/**
 * `0` no prazo desliga a cobrança daquele canal, e é diferente de `null` ("usar o
 * default"). Ponto único da regra para que a seção da política (M4) e qualquer leitura
 * futura contem a mesma história.
 */
export const isActivityRequestChannelOff = (delay: number | null | undefined): boolean =>
  delay === 0

/** Mesmos limites do upload da folha — não criar duas regras (M2, erro A). */
export const ACTIVITY_ATTACHMENT_LIMITS = {
  minFiles: 1,
  maxFiles: 10,
  maxFileBytes: 50 * 1024 * 1024,
  maxTotalBytes: 200 * 1024 * 1024,
  allowedExtensions: ['pdf', 'png', 'jpg', 'jpeg']
} as const

/* ------------------------------------------------------------------------- *
 * F4 — Exportação de anexos de Registro de Atividade (M3 ④).
 * Espelha `src/modules/ds-attendance/domain/activity-export.constants.ts` do
 * backend: rota própria (`containers/attachments/export`), permissão
 * `attendance_extracting` e SÓ dois formatos.
 * ------------------------------------------------------------------------- */

/**
 * Partes que compõem o nome de cada anexo no ZIP. A ORDEM escolhida pelo
 * usuário é a ordem do nome do arquivo — por isso é um array e não um set.
 * Lista vazia = o backend aplica `DEFAULT_ACTIVITY_EXPORT_NAME` (as quatro,
 * nesta ordem).
 */
export enum ExportNamePart {
  DOCTOR = 'DOCTOR',
  STATION_DATE = 'STATION_DATE',
  LEG = 'LEG',
  ACTIVITY_TYPE = 'ACTIVITY_TYPE'
}

export const EXPORT_NAME_PART_LABELS: Record<ExportNamePart, string> = {
  [ExportNamePart.DOCTOR]: 'Médico',
  [ExportNamePart.STATION_DATE]: 'Data do plantão',
  [ExportNamePart.LEG]: 'Perna (check-in / check-out / ambas)',
  [ExportNamePart.ACTIVITY_TYPE]: 'Tipo do Registro de Atividade'
}

/** Ordem default do backend (`DEFAULT_ACTIVITY_EXPORT_NAME`), para o modal abrir marcado. */
export const DEFAULT_EXPORT_NAME_PARTS: ExportNamePart[] = [
  ExportNamePart.DOCTOR,
  ExportNamePart.STATION_DATE,
  ExportNamePart.LEG,
  ExportNamePart.ACTIVITY_TYPE
]

/**
 * `pdf` NÃO existe na rota de anexos: o serviço do backend só monta ZIP ou XLSX
 * e a DTO recusa qualquer outro valor com 400 (T4, divergência 1). Oferecer o
 * PDF no modal seria oferecer um erro.
 */
export const ATTACHMENT_EXPORT_FORMATS = ['zip', 'xlsx'] as const
export type AttachmentExportFormat = (typeof ATTACHMENT_EXPORT_FORMATS)[number]

export const ATTACHMENT_EXPORT_FORMAT_LABELS: Record<AttachmentExportFormat, string> = {
  zip: 'ZIP com os anexos',
  xlsx: 'Planilha (XLSX) com o guia'
}

/**
 * Código do 400 que a rota ANTIGA (`containers/export`) devolve quando o recorte
 * pede `kind=ATIVIDADE`: exportar anexo de atividade é `attendance_extracting` e
 * mora na rota nova. A tela traduz o código para "use Exportar anexos".
 */
export const ACTIVITY_EXPORT_REQUIRES_EXTRACTING = 'ACTIVITY_EXPORT_REQUIRES_EXTRACTING'

/** 400 do teto de anexos (`{ code, attachments, max }`) — o modal mostra os números. */
export const EXPORT_TOO_LARGE = 'EXPORT_TOO_LARGE'

/**
 * Quarto estado da coluna "Reg. Plantão" do pós-plantão (M5, balão ⑤): sem
 * política de presença vigente na data do plantão o domínio sai `PENDING` e a
 * tela precisa dizer que NÃO foi avaliado — diferente de "avaliado e sem
 * registro", que é `NONE` / "Sem registro".
 */
export const REGISTRATION_NOT_EVALUATED_LABEL = 'Não avaliado'

/**
 * Q3 (spec 2026-09-25 §6.6) — médico fora do time nunca precisa fazer check no app. O
 * backend força `required_check_not_our_team = false` na política e na escala; a tela
 * mostra o controle desabilitado com este motivo em texto visível.
 */
export const NOT_OUR_TEAM_CHECK_DISABLED_REASON =
  'Médicos fora do time não precisam fazer check no app (decisão de 24/09/2026).'
